import type {
  AnyRouter,
  AnySchema,
  ParsedLocation,
} from "@tanstack/react-router";

// @sentry/react's tanstack router integration names pageload spans from the raw
// `window.location.pathname`, which still carries the router basepath, while the
// router itself matches on basepath-stripped locations. Our apps are mounted under
// `window._msBaseUrl` (e.g. `/dashboard_new/lcm`), so those extra segments get eaten
// by catch-all dynamic routes and v2 pages end up reported under legacy v1 route ids.
// Stripping the basepath before matching keeps transaction names on the real route.
// Upstream: https://github.com/getsentry/sentry-javascript/issues/23253
export function basepathAwareRouter(router: AnyRouter): AnyRouter {
  const basepath = normalizeBasepath(router.basepath);
  if (!basepath) {
    return router;
  }

  const matchRoutes = router.matchRoutes.bind(router);

  return new Proxy(router, {
    get(target, property) {
      if (property === "matchRoutes") {
        return function stripBasepathAndMatch(
          pathnameOrNext: string | ParsedLocation,
          ...rest: [
            locationSearch?: AnySchema,
            opts?: { throwOnError?: boolean },
          ]
        ) {
          return matchRoutes(
            typeof pathnameOrNext === "string"
              ? stripBasepath(pathnameOrNext, basepath)
              : pathnameOrNext,
            ...rest,
          );
        };
      }
      // eslint-disable-next-line mobsuccess/member-expressions
      const value = Reflect.get(target, property, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}

function normalizeBasepath(basepath: string | undefined): string | null {
  const trimmed = basepath?.replace(/\/+$/, "") ?? "";
  return trimmed && trimmed !== "/" ? trimmed : null;
}

function stripBasepath(pathname: string, basepath: string): string {
  if (!pathname.startsWith(basepath)) {
    return pathname;
  }
  const rest = pathname.slice(basepath.length);
  if (rest && !rest.startsWith("/")) {
    return pathname;
  }
  return rest || "/";
}
